import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import i18n from "@/i18n";

export interface PetGroomingProfile {
  id: string;
  pet_id: string;
  customer_id: string;
  coat_type: string | null;
  temperament: string | null;
  fears: string[];
  allergies: string[];
  skin_issues: string | null;
  sensitive_areas: string[];
  preferred_style: string | null;
  style_photos: string[];
  special_instructions: string | null;
  created_at: string;
  updated_at: string;
}

export interface PetGroomingProfileInput {
  pet_id: string;
  coat_type?: string | null;
  temperament?: string | null;
  fears?: string[];
  allergies?: string[];
  skin_issues?: string | null;
  sensitive_areas?: string[];
  preferred_style?: string | null;
  style_photos?: string[];
  special_instructions?: string | null;
}

export const usePetGroomingProfile = (petId: string) => {
  return useQuery({
    queryKey: ["pet-grooming-profile", petId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pet_grooming_profiles")
        .select("*")
        .eq("pet_id", petId)
        .maybeSingle();
      
      if (error) throw error;
      return data as PetGroomingProfile | null;
    },
    enabled: !!petId,
  });
};

export const useUpsertPetGroomingProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (input: PetGroomingProfileInput) => {
      const customerId = await getCurrentClinicCustomerId();
      
      const { data, error } = await supabase
        .from("pet_grooming_profiles")
        .upsert({
          ...input,
          customer_id: customerId,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pet-grooming-profile", data.pet_id] });
      toast({ title: i18n.t("grooming:phase8.petGroomingProfile.savedTitle") });
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("grooming:phase8.petGroomingProfile.saveErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const usePetSizeCategories = () => {
  return useQuery({
    queryKey: ["pet-size-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pet_size_categories")
        .select("*")
        .order("display_order");
      
      if (error) throw error;
      return data;
    },
  });
};
